import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, X } from 'lucide-react';
import { sendHtmlEmail } from '../emailapi';

const initialFormData = {
  fullName: '',
  email: '',
  phone: '',
  service: '',
  message: '',
  acceptsTerms: false,
  acceptsUpdates: false,
};

const ContactPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsOpen(true), 900);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await sendHtmlEmail('cashprodigital@gmail.com', 'New Enquiry from Home Popup', {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        acceptsTerms: formData.acceptsTerms ? 'Yes' : 'No',
        acceptsUpdates: formData.acceptsUpdates ? 'Yes' : 'No',
      });

      setSubmitStatus('success');
      setFormData(initialFormData);
      alert('Thank you! We will get back to you soon.');
      setIsOpen(false);
    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitStatus('error');
      alert('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-gray-950/60 px-3 py-4 sm:px-6 sm:py-6">
      <button
        type="button"
        className="fixed inset-0 cursor-default"
        onClick={() => setIsOpen(false)}
        aria-label="Close contact form"
      />

      <div className="relative w-full max-w-lg rounded-2xl border border-green-100 bg-white p-4 shadow-2xl sm:p-5">
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="absolute right-3 top-3 rounded-full p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
          aria-label="Close popup"
        >
          <X size={20} />
        </button>

        <h3 className="mb-4 pr-10 text-2xl font-bold text-gray-900 sm:text-3xl">
          Send Us a Message
        </h3>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700 sm:text-sm">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-green-500 sm:text-base"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700 sm:text-sm">
              Email Address (Optional)
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-green-500 sm:text-base"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700 sm:text-sm">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-green-500 sm:text-base"
              placeholder="+91 98765 43210"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700 sm:text-sm">
              Select Required Service <span className="text-red-500">*</span>
            </label>
            <select
              name="service"
              required
              value={formData.service}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition focus:border-transparent focus:ring-2 focus:ring-green-500 sm:text-base"
            >
              <option value="">Select a service</option>
              <option value="Personal Loan">Personal Loan</option>
              <option value="Business Loan">Business Loan</option>
              <option value="Corporate Funding">Corporate Funding</option>
              <option value="Home Loan">Home Loan</option>
              <option value="Commercial Property Purchase">Commercial Property Purchase</option>
              <option value="Industrial Property Purchase">Industrial Property Purchase</option>
              <option value="Machinery Loan">Machinery Loan</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700 sm:text-sm">
              Tell us about your requirements...
            </label>
            <textarea
              name="message"
              rows="2"
              value={formData.message}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-green-500 sm:text-base"
              placeholder="I need a loan for..."
            />
          </div>

          <div className="space-y-2">
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-gray-900 shadow-sm transition hover:border-green-200 hover:bg-green-50">
              <input
                type="checkbox"
                name="acceptsTerms"
                required
                checked={formData.acceptsTerms}
                onChange={handleChange}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-400 text-green-600 focus:ring-2 focus:ring-green-500"
              />
              <span className="text-sm leading-relaxed">
                I agree to all the{' '}
                <Link to="/terms-and-conditions" className="font-semibold text-green-600 underline underline-offset-2 hover:text-green-700">
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="font-semibold text-green-600 underline underline-offset-2 hover:text-green-700">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-gray-900 shadow-sm transition hover:border-green-200 hover:bg-green-50">
              <input
                type="checkbox"
                name="acceptsUpdates"
                checked={formData.acceptsUpdates}
                onChange={handleChange}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-400 text-green-600 focus:ring-2 focus:ring-green-500"
              />
              <span className="text-sm leading-relaxed">
                I want to receive CashPro updates, promotions, and blog updates via Email, SMS, WhatsApp, and RCS messages.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !formData.acceptsTerms}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-green-600 to-green-700 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:from-green-700 hover:to-green-800 disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
          >
            {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
            {!isSubmitting && <Send size={18} />}
          </button>

          {submitStatus === 'error' && (
            <p className="text-center text-sm text-red-600">Failed to send. Please try again.</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ContactPopup;
